import React, { useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trophy, ArrowBigUp, Upload, ArrowLeft, Wand2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import BadgeTag from "@/components/BadgeTag";
import Countdown from "@/components/Countdown";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/lib/assets";

export default function BattleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef(null);

  const { data: b, isLoading } = useQuery({
    queryKey: ["battle", id], queryFn: async () => (await api.get(`/battles/${id}`)).data,
  });

  const vote = useMutation({
    mutationFn: (sid) => api.post(`/battles/${id}/vote/${sid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["battle", id] }); toast.success("Vote counted."); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const submit = useMutation({
    mutationFn: (file) => {
      const fd = new FormData(); fd.append("file", file); fd.append("caption", "");
      return api.post(`/battles/${id}/submit`, fd);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["battle", id] }); toast.success("Entry submitted. Good luck."); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading) return <p className="font-mono text-dim max-w-7xl mx-auto px-6 py-16">Loading...</p>;
  if (!b) return null;

  const onVote = (sid) => { if (!user) { toast.error("Log in to vote."); navigate("/login"); return; } vote.mutate(sid); };
  const onPick = () => { if (!user) { toast.error("Log in to enter."); navigate("/login"); return; } fileRef.current?.click(); };
  const onCreate = () => { if (!user) { toast.error("Log in to enter."); navigate("/login"); return; } navigate(`/memes?battle=${b.id}`); };
  const canVote = ["active", "voting"].includes(b.status);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      <Link to="/battles" className="flex items-center gap-1 font-mono text-xs text-dim hover:text-neon"><ArrowLeft className="w-3.5 h-3.5" /> All Battles</Link>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-4">
        <div>
          <span className="font-mono text-[10px] text-dim">BATTLE #{String(b.number || 1).padStart(3, "0")}</span>
          <h1 className="headline text-cream text-3xl sm:text-5xl mt-1">{b.title}</h1>
          {b.prompt && <p className="font-mono text-dim mt-3">{b.prompt}</p>}
          {["active", "voting"].includes(b.status) && b.endsAt && <div className="mt-2"><Countdown endsAt={b.endsAt} /></div>}
        </div>
        {b.status === "active" && !b.mySubmission && (
          <div className="flex flex-col sm:flex-row gap-2 shrink-0" data-testid="battle-entry-actions">
            <button onClick={onCreate} className="btn-neon" data-testid="battle-create-studio"><Wand2 className="w-4 h-4" /> Create In Studio</button>
            <button onClick={onPick} disabled={submit.isPending} className="btn-ghost !border-neon hover:!bg-neon disabled:opacity-60" data-testid="battle-submit">
              {submit.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload A Meme</>}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => { if (e.target.files[0]) { submit.mutate(e.target.files[0]); e.target.value = ""; } }} data-testid="battle-file" />
          </div>
        )}
        {b.status === "active" && b.mySubmission && (
          <span className="tag" data-testid="battle-entered">✓ You're in this battle</span>
        )}
      </div>

      {b.status === "ended" && (
        <div className="comic-card p-4 mt-6 bg-[#1a1500] border-gold flex items-center gap-3">
          <Trophy className="w-6 h-6 text-gold" />
          <p className="font-display font-900 text-gold uppercase">{b.winnerSubmissionId ? "Winner crowned — see the 🏆 below" : "Battle ended"}</p>
        </div>
      )}

      {b.submissions?.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-center">
          <img src={ASSETS.pointing} alt="empty" className="w-36" />
          <p className="font-display text-cream mt-4 uppercase">No entries yet. Be the first.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {b.submissions.map((s) => {
            const isWinner = b.winnerSubmissionId === s.id;
            return (
              <div key={s.id} className={`comic-card p-4 ${isWinner ? "border-gold" : ""}`} data-testid={`sub-${s.id}`}>
                {isWinner && <div className="flex items-center gap-1 text-gold font-display font-900 uppercase text-sm mb-2"><Trophy className="w-4 h-4" /> Winner</div>}
                <img src={`${process.env.REACT_APP_BACKEND_URL}${s.image_url}`} alt="entry" className="w-full max-h-72 object-contain border-3 border-black bg-white" />
                <div className="flex items-center justify-between mt-3">
                  <span className="flex items-center gap-1.5 min-w-0">
                    {s.user_id
                      ? <Link to={`/u/${s.user_id}`} className="font-mono text-[11px] text-dim hover:text-neon truncate">@{s.username}</Link>
                      : <span className="font-mono text-[11px] text-dim truncate">@{s.username}</span>}
                    <BadgeTag rob={s.rob} />
                  </span>
                  <button onClick={() => onVote(s.id)} disabled={!canVote || b.myVote}
                    className={`flex items-center gap-1.5 border-3 border-black px-3 py-1.5 font-display font-900 transition-colors ${b.myVote === s.id ? "bg-neon text-black" : "bg-ink3 text-neon hover:bg-neon hover:text-black"} disabled:opacity-50`}
                    data-testid={`vote-${s.id}`}>
                    <ArrowBigUp className="w-4 h-4" /> {s.votes}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
